import React, { useMemo } from "react";
import { EuiIcon, EuiTabbedContent } from "@elastic/eui";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { useParams } from "react-router-dom";
import { ProfileEntitiesWrapper } from "../entity/components/EntityProfile/style";
import ProfileTabName from "../entity/components/EntityProfile/ProfileTabName";
import ProfileEntity from "../entity/components/EntityProfile/ProfileEntity";
import { EntityProfileContext } from "../entity/components/EntityProfile";
import ProfileStats from "../entity/components/EntityProfile/ProfileStats";
import { entitySelector, updateEntity } from "../entity/slice";
import { extractResourceAttributes } from "../../../utils/jsonApiUtils";
import { singular } from "../../../utils/stringUtils";
import Api from "../../../lib/api";

function CustomTable({ entityOptions, customTabs }) {
    const { entityName, displayName, profileFields, statsFields } = entityOptions;
    const { data, included } = useSelector(entitySelector(entityName));
    const { id } = useParams();
    const statsTab = {
        name: (
            <ProfileTabName
                label="Statistics"
                icon={
                    <EuiIcon
                        size="s"
                        type="stats"
                    />
                }
            />
        ),
        id: "stats",
        content: <ProfileStats />,
    };
    const generateTabs = (relationFields, customTabs, showStats) => [
        ...relationFields.map((field) => ({
            name: (
                <ProfileTabName
                    entityName={field.entityName}
                    label={field.label}
                />
            ),
            id: field.name,
            content: <ProfileEntity field={field} />,
        })),
        ...(customTabs?.map((tab) => ({
            name: (
                <ProfileTabName
                    hideIcon
                    label={tab.label}
                />
            ),
            id: tab.label.toLowerCase().replaceAll(" ", "-"),
            content: tab.component,
        })) || []),
        ...(showStats ? [statsTab] : []),
    ];

    const updateProfileEntity = async ({ entityName, entityId, attributes }) => {
        await Api.put(`/${_.kebabCase(entityName)}/${entityId}`, {
            data: {
                id: +entityId,
                type: entityName,
                attributes,
            },
        });
    };

    const deleteAttachment = async (attachmentId) => {
        const response = await Api.delete(`attachments/${attachmentId}`);
        return response;
    };

    const addAttachment = async ({ entityName, entityId, attributes }) => {
        const updateAttachmentsPromise = attributes.attachment.map(async (document) => {
            const body = {
                data: {
                    attributes: {
                        document,
                        attachedItemId: +entityId,
                        attachedItemType: _.capitalize(singular(entityName)),
                    },
                    type: "attachments",
                },
            };

            const response = await Api.post("attachments", body);
            return response;
        });

        return await Promise.all(updateAttachmentsPromise);
    };

    const handleUpdate = async ({
        isProfileEntity,
        entity: { entityName, entityId, attributes },
        callback,
        dispatch,
        fetch,
    }) => {
        if (_.isNil(entityName) || _.isNil(entityId)) {
            callback();
            return;
        }

        try {
            isProfileEntity
                ? await updateProfileEntity({ entityName, entityId, attributes })
                : attributes.attachment
                ? attributes.attachment.id
                    ? await deleteAttachment(attributes.attachment.id)
                    : await addAttachment({ entityName, entityId, attributes })
                : await dispatch(updateEntity(entityName, entityId, attributes));

            await fetch();
            callback();
        } catch (error) {
            console.log(error);
            callback();
        }
    };

    const relationFields = useMemo(
        () => profileFields.filter((field) => field.type === "relation"),
        [profileFields]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const renderTabs = useMemo(
        () => generateTabs(relationFields, customTabs, !!statsFields),
        [relationFields, customTabs, statsFields]
    );
    const statsIndex = useMemo(
        () => renderTabs.findIndex((tab) => tab.id === "stats"),
        [renderTabs]
    );

    const dispatch = useDispatch();
    return (
        <EntityProfileContext.Provider
            value={{
                entityResource: data,
                entity: extractResourceAttributes(data),
                includedResource: included,
                entityName,
                entityDisplayName: displayName || entityName,
                entityId: id,
                fields: profileFields,
                statsFields,
                relationFields,
                fetch,
                onSubmit: (attributes, callback) =>
                    handleUpdate({
                        entity: { entityName, entityId: id, attributes },
                        callback,
                        dispatch,
                        fetch,
                    }),
                onEntityUpdate: ({ entityName, entityId, attributes }, callback) =>
                    handleUpdate({
                        isProfileEntity: true,
                        entity: { entityName, entityId, attributes },
                        callback,
                        dispatch,
                        fetch,
                    }),
            }}
        >
            <ProfileEntitiesWrapper paddingSize="none">
                <EuiTabbedContent
                    tabs={renderTabs}
                    expand={false}
                    initialSelectedTab={renderTabs[statsIndex > -1 ? statsIndex : 0]}
                    autoFocus="selected"
                />
            </ProfileEntitiesWrapper>
        </EntityProfileContext.Provider>
    );
}

export default CustomTable;
